import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { PrimeLevel } from "./PrimeLevel";
import { toast } from "sonner";

const H = "px-2 py-1.5 text-[12px] font-semibold text-center leading-tight border-r border-white/60";
const n2 = (v) => (v === null || v === undefined ? "" : Number(v).toFixed(2));

export function PrimeResultsTable({ org, reloadKey }) {
  const [period, setPeriod] = useState("");
  const [d, setD] = useState(null);
  const load = useCallback(() => {
    api.get("/prime/results", { params: { organization_id: org, period } }).then((r) => setD(r.data)).catch((e) => toast.error(apiError(e)));
  }, [org, period]);
  useEffect(() => { load(); }, [load, reloadKey]);
  if (!d) return <p className="text-sm text-slate-400">Loading…</p>;
  const kpis = d.kpis.filter((k) => d.items.some((r) => r.kpi_points?.[k.key] !== undefined));
  return (
    <div className="space-y-4" data-testid="prime-results">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <h2 className="text-2xl sm:text-3xl font-bold text-[#0B4DA2]">Prime Agency Program</h2>
        <label className="border border-slate-400 px-3 py-2 text-xs font-semibold text-slate-800">Reporting Date
          <select value={d.period || ""} onChange={(e) => setPeriod(e.target.value)} className="block mt-1 h-8 text-sm font-normal border-0 border-b border-slate-300 bg-transparent" data-testid="prime-results-period">
            {d.periods.length === 0 && <option value="">No results yet</option>}
            {d.periods.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}</select></label>
      </div>
      {d.items.length === 0 ? <p className="text-sm text-slate-500" data-testid="prime-results-empty">No Prime results have been imported for this group yet.</p> : (
        <div className="overflow-auto border border-slate-300 max-h-[70vh]">
          <table className="min-w-[1100px] w-full text-[13px]">
            <thead className="sticky top-0 z-10"><tr>
              {["Agency Number", "UPN", "Agency Owner Name", "Tenure"].map((h) => <th key={h} className={`${H} bg-[#B4C6E7] text-slate-900`}>{h}</th>)}
              <th className={`${H} bg-[#E31837] text-white`}>Achievement Level</th>
              {["% of Possible KPI Points Achieved", "Points Achieved", "Points Possible"].map((h) => <th key={h} className={`${H} bg-[#F8CBD3] text-slate-900`}>{h}</th>)}
              <th className={`${H} bg-[#002B7F] text-white`}>PIF Band</th>
              {kpis.map((k) => <th key={k.key} className={`${H} bg-[#0070C0] text-white`}>{k.name} Points Achieved</th>)}
            </tr></thead>
            <tbody>{d.items.map((r, i) => (
              <tr key={r.business_id} className={i % 2 ? "bg-slate-100" : "bg-white"} data-testid={`prime-results-row-${r.business_id}`}>
                <td className="px-2 py-1 border-r border-slate-200">{r.agent_code}</td>
                <td className="px-2 py-1 border-r border-slate-200">{r.upn}</td>
                <td className="px-2 py-1 border-r border-slate-200">{r.owner_person_id ? <Link to={`/people/detail/${r.owner_person_id}`} className="hover:underline">{r.owner_names.join(", ") || r.business_name}</Link> : r.business_name}</td>
                <td className="px-2 py-1 text-right border-r border-slate-200">{r.tenure}</td>
                <td className="px-2 py-1 border-r border-slate-200"><PrimeLevel level={r.level} /></td>
                <td className="px-2 py-1 text-right border-r border-slate-200">{r.score_pct != null ? `${Number(r.score_pct).toFixed(2)}%` : "Not recorded"}</td>
                <td className="px-2 py-1 text-right border-r border-slate-200">{n2(r.achieved_points)}</td>
                <td className="px-2 py-1 text-right border-r border-slate-200">{n2(r.available_points)}</td>
                <td className="px-2 py-1 border-r border-slate-200">{r.pif_band}</td>
                {kpis.map((k) => <td key={k.key} className="px-2 py-1 text-right border-r border-slate-200">{n2(r.kpi_points?.[k.key])}</td>)}
              </tr>))}</tbody>
          </table>
        </div>)}
      <p className="text-[11px] text-slate-500">Imported from Farmers' Prime reporting, which remains the official record. Percentages are stored to one decimal place.</p>
    </div>
  );
}
